import * as THREE from "three";

/**
 * Owns the camera and its damped follow behavior. All camera motion in the
 * app goes through this rig rather than touching `THREE.PerspectiveCamera`
 * directly from unrelated animation code (section 20).
 */
export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  private currentLookAt = new THREE.Vector3(0, 0, 0);

  constructor(aspect: number) {
    // Wider than a typical close-up FOV, paired with CameraAnimation's
    // pulled-back chase distance, so the full 8x8 board reads clearly
    // through a move rather than a tight shot cropping the far corners.
    this.camera = new THREE.PerspectiveCamera(46, aspect, 0.1, 100);
    this.camera.position.set(0, 7.5, 9.5);
    this.camera.lookAt(this.currentLookAt);
  }

  updateAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /** Instant cut, used only when a tour resets and there is nothing worth easing from. */
  snapTo(position: THREE.Vector3, lookAt: THREE.Vector3): void {
    this.camera.position.copy(position);
    this.currentLookAt.copy(lookAt);
    this.camera.lookAt(this.currentLookAt);
  }

  /**
   * Exponential (frame-rate independent) damping toward a desired
   * position/look-at. `damping` is a rate, not a fixed step, so the same
   * call produces consistent motion regardless of the caller's delta time.
   *
   * The effective rate is reduced the more the camera has to swing around
   * its focal point: a near-180° reorientation (the largest possible angle
   * between two directions from the same pivot — a near-360°-feeling swing
   * the "short way" is the same angle as the "long way") settles noticeably
   * slower than a small nudge, instead of both completing in the same
   * wall-clock time and making big turns feel like a fast whip-pan.
   */
  followTarget(desiredPosition: THREE.Vector3, desiredLookAt: THREE.Vector3, dt: number, baseDamping = 6): void {
    const currentDirection = this.camera.position.clone().sub(this.currentLookAt);
    const desiredDirection = desiredPosition.clone().sub(this.currentLookAt);

    let angularSlowdown = 1;
    if (currentDirection.lengthSq() > 1e-6 && desiredDirection.lengthSq() > 1e-6) {
      const angle = currentDirection.angleTo(desiredDirection); // 0..PI
      const MAX_SLOWDOWN_FRACTION = 0.65;
      angularSlowdown = 1 - MAX_SLOWDOWN_FRACTION * (angle / Math.PI);
    }

    const damping = baseDamping * angularSlowdown;
    const alpha = 1 - Math.exp(-damping * dt);
    this.camera.position.lerp(desiredPosition, alpha);
    this.currentLookAt.lerp(desiredLookAt, alpha);
    this.camera.lookAt(this.currentLookAt);
  }
}
